import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { FormSection } from '@/components/ui/form-section';
import { Input } from '@/components/ui/input';
import { MarkdownEditor } from '@/components/ui/markdown-editor';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ApiError } from '@/core/api';
import { OPENING_STATUSES } from '../constants';
import {
  createDepartmentName,
  resolveDepartmentName,
  searchDepartmentNames,
} from '../departmentSelect';
import { useCreateOpening, useOpening, useUpdateOpening } from '../queries';
import type { JobOpeningInput } from '../types';

const OPENINGS_PATH = '/hrm/recruitment';

const schema = z.object({
  title: z.string().trim().min(1, 'Title is required').max(255),
  department: z.string().max(150),
  description: z.string(),
  external_url: z.string().trim().url('Enter a valid URL').or(z.literal('')),
  status: z.enum(['open', 'on_hold', 'closed', 'filled']),
  openings_count: z
    .string()
    .refine((v) => !Number.isNaN(Number(v)) && Number(v) >= 1, 'Enter ≥ 1'),
});

type FormValues = z.infer<typeof schema>;

export function NewOpeningPage() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const create = useCreateOpening();
  const update = useUpdateOpening();
  const { data: existing } = useOpening(editId ?? '');
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: '', department: '', description: '', external_url: '', status: 'open', openings_count: '1',
    },
  });

  // Prefill the form once the opening loads (edit mode).
  useEffect(() => {
    if (existing) {
      form.reset({
        title: existing.title,
        department: existing.department,
        description: existing.description,
        external_url: existing.external_url,
        status: existing.status,
        openings_count: String(existing.openings_count),
      });
    }
  }, [existing, form]);

  const backTo = isEdit ? `${OPENINGS_PATH}/${editId}` : OPENINGS_PATH;

  async function handleSubmit(values: FormValues) {
    const input: JobOpeningInput = {
      title: values.title,
      department: values.department,
      description: values.description,
      external_url: values.external_url,
      status: values.status,
      openings_count: Number(values.openings_count),
    };
    try {
      if (isEdit && editId) {
        await update.mutateAsync({ id: editId, input });
      } else {
        await create.mutateAsync(input);
      }
      navigate(backTo);
    } catch (e) {
      const err = e as ApiError;
      if (err?.fieldErrors) {
        for (const [field, messages] of Object.entries(err.fieldErrors)) {
          form.setError(field as keyof FormValues, { message: messages.join(' ') });
        }
      }
    }
  }

  return (
    <div className="w-full max-w-3xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(backTo)}>
          <ArrowLeft />
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isEdit ? 'Edit job opening' : 'New job opening'}
        </h1>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
          <FormSection variant="card" title="Job">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="Senior backend engineer" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="department"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Department</FormLabel>
                  <FormControl>
                    <EntitySelect
                      queryKey="opening-department-picker"
                      value={field.value}
                      onChange={field.onChange}
                      search={searchDepartmentNames}
                      resolveLabel={resolveDepartmentName}
                      onCreate={createDepartmentName}
                      placeholder="No department"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="external_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>External link</FormLabel>
                  <FormControl>
                    <Input
                      type="url"
                      placeholder="https://linkedin.com/jobs/… (where it's published)"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <FormSection variant="card" title="Status">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status</FormLabel>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {OPENING_STATUSES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="openings_count"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Headcount</FormLabel>
                    <FormControl>
                      <Input type="number" min="1" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </FormSection>
          <FormSection variant="card" title="Description">
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <MarkdownEditor
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Role summary, responsibilities, requirements…"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FormSection>
          <div className="flex gap-2">
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting
                ? 'Saving…'
                : isEdit
                  ? 'Save changes'
                  : 'Create opening'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate(backTo)}>
              Cancel
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
